import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ImagePlus, Loader2, Phone, X } from "lucide-react";
import { BUSINESS, FREQUENCY_OPTIONS, PROPERTY_TYPES, SERVICE_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  fullName: "",
  phone: "",
  email: "",
  propertyType: "",
  service: "",
  frequency: "",
  bedrooms: "",
  bathrooms: "",
  propertySize: "",
  preferredDate: "",
  preferredTime: "",
  notes: "",
  consent: false,
};

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="mt-1.5 text-xs font-semibold text-red-600">
      {message}
    </p>
  ) : null;

export const QuoteForm = () => {
  const [params] = useSearchParams();
  const prefill = params.get("service");
  const [form, setForm] = useState(() => ({
    ...initial,
    service: SERVICE_OPTIONS.includes(prefill) ? prefill : "",
  }));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const fileInput = useRef(null);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const addFiles = (list) => {
    const valid = Array.from(list).filter(
      (f) => f.type.startsWith("image/") && f.size <= 8 * 1024 * 1024
    );
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
    if (!/^[\d\s+()\-]{8,}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.propertyType) e.propertyType = "Please select a property type.";
    if (!form.service) e.service = "Please select a cleaning service.";
    if (!form.frequency) e.frequency = "Please select a cleaning frequency.";
    if (!form.consent) e.consent = "Please confirm we can contact you about your enquiry.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      document.querySelector('[aria-invalid="true"]')?.focus();
      return;
    }
    setStatus("sending");
    try {
      const fd = new FormData();
      fd.append("full_name", form.fullName);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("property_type", form.propertyType);
      fd.append("service", form.service);
      fd.append("frequency", form.frequency);
      fd.append("bedrooms", form.bedrooms);
      fd.append("bathrooms", form.bathrooms);
      fd.append("property_size", form.propertySize);
      fd.append("preferred_date", form.preferredDate);
      fd.append("preferred_time", form.preferredTime);
      fd.append("notes", form.notes);
      fd.append("consent", String(form.consent));
      files.forEach((f) => fd.append("photos", f));
      await axios.post(`${API}/enquiries`, fd);
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="quote-success-message"
        className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-10 text-center sm:p-14"
      >
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
        <h3 className="mt-6 text-2xl font-bold tracking-tight sm:text-3xl">Thank you!</h3>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed">
          We've received your cleaning enquiry. We'll review the details and get back to you as soon as possible.
        </p>
        <p className="mt-6 text-sm">
          Need us sooner? Call{" "}
          <a href={BUSINESS.phoneHref} className="font-bold text-emerald-700 underline underline-offset-2">
            {BUSINESS.phone}
          </a>
        </p>
        <button
          type="button"
          data-testid="quote-submit-another-button"
          onClick={() => {
            setForm(initial);
            setFiles([]);
            setStatus("idle");
          }}
          className="btn-outline mt-8 text-sm"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={onSubmit}
      noValidate
      className="rounded-3xl border border-slate-100 bg-white p-7 shadow-2xl shadow-sky-900/5 sm:p-10"
    >
      {status === "error" && (
        <div
          data-testid="quote-error-message"
          role="alert"
          className="mb-8 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm leading-relaxed text-red-800"
        >
          Something went wrong while sending your enquiry. Please try again or call us directly at{" "}
          <a href={BUSINESS.phoneHref} className="font-bold underline underline-offset-2">
            {BUSINESS.phone}
          </a>
          .
        </div>
      )}

      <fieldset>
        <legend className="font-heading text-lg font-bold text-navy-800">Contact Details</legend>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="fullName" className="field-label">Full Name *</label>
            <input
              id="fullName"
              data-testid="quote-fullname-input"
              type="text"
              autoComplete="name"
              className={`input-field ${errors.fullName ? "input-error" : ""}`}
              value={form.fullName}
              onChange={(e) => set("fullName", e.target.value)}
              aria-invalid={!!errors.fullName}
              aria-describedby="fullName-error"
              placeholder="Your full name"
            />
            <FieldError id="fullName-error" message={errors.fullName} />
          </div>
          <div>
            <label htmlFor="phone" className="field-label">Phone Number *</label>
            <input
              id="phone"
              data-testid="quote-phone-input"
              type="tel"
              autoComplete="tel"
              className={`input-field ${errors.phone ? "input-error" : ""}`}
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              aria-invalid={!!errors.phone}
              aria-describedby="phone-error"
              placeholder="04XX XXX XXX"
            />
            <FieldError id="phone-error" message={errors.phone} />
          </div>
          <div>
            <label htmlFor="email" className="field-label">Email Address *</label>
            <input
              id="email"
              data-testid="quote-email-input"
              type="email"
              autoComplete="email"
              className={`input-field ${errors.email ? "input-error" : ""}`}
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              aria-invalid={!!errors.email}
              aria-describedby="email-error"
              placeholder="you@example.com"
            />
            <FieldError id="email-error" message={errors.email} />
          </div>
        </div>
      </fieldset>

      <fieldset className="mt-9">
        <legend className="font-heading text-lg font-bold text-navy-800">Property Details</legend>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="propertyType" className="field-label">Property Type *</label>
            <select
              id="propertyType"
              data-testid="quote-property-type-select"
              className={`input-field ${errors.propertyType ? "input-error" : ""}`}
              value={form.propertyType}
              onChange={(e) => set("propertyType", e.target.value)}
              aria-invalid={!!errors.propertyType}
              aria-describedby="propertyType-error"
            >
              <option value="">Select property type</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <FieldError id="propertyType-error" message={errors.propertyType} />
          </div>
          <div>
            <label htmlFor="service" className="field-label">Cleaning Service *</label>
            <select
              id="service"
              data-testid="quote-service-select"
              className={`input-field ${errors.service ? "input-error" : ""}`}
              value={form.service}
              onChange={(e) => set("service", e.target.value)}
              aria-invalid={!!errors.service}
              aria-describedby="service-error"
            >
              <option value="">Select a service</option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <FieldError id="service-error" message={errors.service} />
          </div>
          <div>
            <label htmlFor="frequency" className="field-label">Cleaning Frequency *</label>
            <select
              id="frequency"
              data-testid="quote-frequency-select"
              className={`input-field ${errors.frequency ? "input-error" : ""}`}
              value={form.frequency}
              onChange={(e) => set("frequency", e.target.value)}
              aria-invalid={!!errors.frequency}
              aria-describedby="frequency-error"
            >
              <option value="">Select frequency</option>
              {FREQUENCY_OPTIONS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <FieldError id="frequency-error" message={errors.frequency} />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label htmlFor="bedrooms" className="field-label">Bedrooms</label>
              <input
                id="bedrooms"
                data-testid="quote-bedrooms-input"
                type="number"
                min="0"
                inputMode="numeric"
                className="input-field"
                value={form.bedrooms}
                onChange={(e) => set("bedrooms", e.target.value)}
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <label htmlFor="bathrooms" className="field-label">Bathrooms</label>
              <input
                id="bathrooms"
                data-testid="quote-bathrooms-input"
                type="number"
                min="0"
                inputMode="numeric"
                className="input-field"
                value={form.bathrooms}
                onChange={(e) => set("bathrooms", e.target.value)}
                placeholder="e.g. 2"
              />
            </div>
          </div>
          <div>
            <label htmlFor="propertySize" className="field-label">Approximate Property Size</label>
            <input
              id="propertySize"
              data-testid="quote-property-size-input"
              type="text"
              className="input-field"
              value={form.propertySize}
              onChange={(e) => set("propertySize", e.target.value)}
              placeholder="e.g. 180 m²"
            />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label htmlFor="preferredDate" className="field-label">Preferred Date</label>
              <input
                id="preferredDate"
                data-testid="quote-preferred-date-input"
                type="date"
                className="input-field"
                value={form.preferredDate}
                onChange={(e) => set("preferredDate", e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="preferredTime" className="field-label">Preferred Time</label>
              <input
                id="preferredTime"
                data-testid="quote-preferred-time-input"
                type="time"
                className="input-field"
                value={form.preferredTime}
                onChange={(e) => set("preferredTime", e.target.value)}
              />
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset className="mt-9">
        <legend className="font-heading text-lg font-bold text-navy-800">Additional Information</legend>
        <div className="mt-5 space-y-5">
          <div>
            <label htmlFor="notes" className="field-label">Anything else we should know?</label>
            <textarea
              id="notes"
              data-testid="quote-notes-textarea"
              rows={4}
              className="input-field resize-y"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Tell us anything else we should know about the property or cleaning requirements."
            />
          </div>

          <div>
            <span className="field-label">Upload Photos (optional)</span>
            <button
              type="button"
              data-testid="quote-photo-upload-button"
              onClick={() => fileInput.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 px-6 py-8 text-sm text-slate-500 transition hover:border-emerald-400 hover:bg-emerald-50/40 hover:text-emerald-700"
            >
              <ImagePlus className="h-7 w-7" />
              <span className="font-semibold">Add photos of your property</span>
              <span className="text-xs">JPG, PNG or WEBP · up to 5 photos · max 8MB each</span>
            </button>
            <input
              ref={fileInput}
              data-testid="quote-photo-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {files.length > 0 && (
              <ul className="mt-4 flex flex-wrap gap-3" data-testid="quote-photo-preview-list">
                {files.map((f, i) => (
                  <li key={i} className="relative">
                    <img
                      src={URL.createObjectURL(f)}
                      alt={`Uploaded property photo ${i + 1}`}
                      className="h-20 w-20 rounded-xl object-cover ring-1 ring-slate-200"
                    />
                    <button
                      type="button"
                      data-testid={`quote-photo-remove-${i}`}
                      aria-label={`Remove photo ${i + 1}`}
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-navy-800 text-white shadow"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed">
              <input
                type="checkbox"
                data-testid="quote-consent-checkbox"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                aria-invalid={!!errors.consent}
                aria-describedby="consent-error"
                className="mt-1 h-5 w-5 rounded border-slate-300 text-emerald-500 accent-emerald-500"
              />
              <span>I agree to be contacted regarding my cleaning enquiry. *</span>
            </label>
            <FieldError id="consent-error" message={errors.consent} />
          </div>
        </div>
      </fieldset>

      <button
        type="submit"
        data-testid="quote-submit-button"
        disabled={status === "sending"}
        className="btn-primary mt-9 w-full text-base sm:text-lg"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending your enquiry…
          </>
        ) : (
          "Request My Free Quote"
        )}
      </button>
      <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-slate-400">
        <Phone className="h-3.5 w-3.5" /> Prefer to talk? Call {BUSINESS.phone} · {BUSINESS.hours}
      </p>
    </form>
  );
};
